import { ClipboardCheck, ClipboardList, Radar, Search } from 'lucide-react';

/**
 * What happens between pressing Run next step and an agent touching a host.
 *
 * Shown on an engagement with no plan, where the operator has the least
 * context and the most to lose by guessing. The paragraph underneath has
 * always ended "Two approvals, not one", which is the one fact people miss,
 * and it is a poor thing to say in prose: a reader skims it as a detail about
 * the workflow rather than as the shape of the workflow.
 *
 * So the diagram says it by making you watch. The pulse travels the rail and
 * STOPS at each gate, holds, and only then releases. Nothing about the stall
 * is orchestrated in JavaScript: there is no state, no timer, and no effect.
 * The hold is a flat span in the keyframe, and the gates run the same 6s clock
 * at an offset, so they cannot drift out of step with it.
 *
 * PURELY DECORATIVE, and marked so. It reflects how the runtime works, not
 * what this engagement is currently doing, and a reader who mistakes it for
 * live state would be badly misled. `aria-hidden` keeps it out of the
 * accessibility tree entirely; the prose below carries the same information.
 */

const STAGES = [
  {
    key: 'lead',
    icon: Search,
    title: 'Lead reads',
    sub: 'scope, rules, what is known',
    anim: 'motion-safe:animate-node-one',
  },
  {
    key: 'recon',
    icon: ClipboardList,
    title: 'Recon plan',
    sub: 'scanners it wants to run',
    gate: 'you approve',
    anim: 'motion-safe:animate-gate-one',
  },
  {
    key: 'scanners',
    icon: Radar,
    title: 'Scanners run',
    sub: 'no agent, no model turn',
    anim: 'motion-safe:animate-node-three',
  },
  {
    key: 'test',
    icon: ClipboardCheck,
    title: 'Test plan',
    sub: 'angles built on what they found',
    gate: 'you approve',
    anim: 'motion-safe:animate-gate-two',
  },
];

/*
 * EVERY node reacts, not just the gates, and each one names its own keyframe
 * above rather than being looked up by row position. A parallel array indexed
 * by order is one reordered stage away from the Scanners node reacting on the
 * Lead's beat, and nothing would fail loudly when it did.
 *
 * They all need a reaction because the pulse rides the rail BEHIND the row:
 * the grid paints after the absolutely-positioned rail, so a dot arriving at a
 * node slides under an opaque circle and disappears. At a gate that is covered
 * by the amber ring, which is doing the explaining anyway. At the two ungated
 * stops nothing happened at all and the dot simply vanished for half a second,
 * which reads as a bug rather than as arrival.
 *
 * Raising the dot above the row was the other option and it is worse: a 7px
 * disc parked on top of a 16px icon obscures the one thing the node is for.
 */

export default function PlanFlow() {
  return (
    <div aria-hidden="true" className="mx-auto mt-5 max-w-2xl select-none">
      <div className="relative">
        {/* The rail, behind the nodes. Inset 12.5% so it begins and ends under
            the CENTRE of the first and last icon: in a four-column grid those
            centres are at 12.5% and 87.5%, which makes the track exactly the
            span between them and the four stops land on 0, 33.333, 66.667 and
            100 percent of it. The keyframe depends on this number.

            WHICH IS WHY THE GRID BELOW HAS NO GAP. A gap narrows every column
            and pulls the outer two centres inward, to 12.05% and 87.95% at
            this width, so the rail no longer reaches them and the pulse parks
            3px off the last node. Measured, not guessed. The columns get their
            breathing room from padding inside each cell instead, which does
            not move a centre. */}
        <div className="absolute left-[12.5%] right-[12.5%] top-5 h-px bg-slate-200" />

        {/* The pulse. `left` is animated as a percentage of this track, which
            is why the track spans the same inset as the rail. */}
        <div className="pointer-events-none absolute left-[12.5%] right-[12.5%] top-5 h-px">
          <div className="relative h-px w-full">
            <span
              className="absolute -top-[3px] h-[7px] w-[7px] -translate-x-1/2 rounded-full bg-wasabi-500
                         shadow-[0_0_0_3px_rgba(72,185,76,0.18)]
                         motion-safe:animate-rail-pulse
                         motion-reduce:left-0 motion-reduce:opacity-40"
            />
          </div>
        </div>

        <div className="relative grid grid-cols-4">
          {STAGES.map(({ key, icon: Icon, title, sub, gate, anim }) => (
            <div key={key} className="flex flex-col items-center px-1 text-center">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full border
                            border-slate-300 bg-white ${anim}
                            ${gate ? 'motion-reduce:border-amber-300 motion-reduce:bg-amber-50' : ''}`}
              >
                <Icon className="h-4 w-4 text-slate-500" />
              </div>
              <p className="mt-2 text-[11px] font-semibold text-slate-700">{title}</p>
              {/* Fixed height for two lines. "angles built on what they
                  found" wraps and the others do not, which pushed one gate's
                  YOU APPROVE a line below the other's. */}
              <p className="mt-0.5 min-h-[24px] text-[10px] leading-tight text-slate-400">{sub}</p>
              {gate ? (
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-amber-600">
                  {gate}
                </p>
              ) : (
                /* Holds the row height so the two gated columns do not sit
                   taller than the two that are not gated. */
                <p className="mt-1 text-[10px] uppercase tracking-wide text-transparent">.</p>
              )}
            </div>
            ))}
        </div>
      </div>
    </div>
  );
}
